import React, { FC, useContext, useEffect, useState } from 'react';
import { RecentList } from '@marriott/mi-ui-library-shop';
import { ContentContext, FormValuesContext, ViewportContext } from '../../index.contexts';
import { RecentViewAndSearchProps } from './recentViewAndSearch.types';
import TertiaryCta from '../../atoms/tertiaryCta/tertiaryCta';
import { setDestinationHiddenFields } from '../../../store/store.actions';
import { StyledRecentViewAndSearch } from './recentViewAndSearch.styles';
import clsx from 'clsx';
import CurrentLocationView from '../currentLocation/currentLocation';
import { CurrentLocationFeature } from '@marriott/mi-ui-library';
import { usePageContext } from '../../../../../../context/PageContext';
import { isCNLocale } from '@marriott/shared/mi-helper-utils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

const RecentViewAndSearch: FC<RecentViewAndSearchProps> = ({
  updateInputValue,
  closePopup,
  updateSearchView,
  showListView,
}) => {
  const { setFormValues } = useContext(FormValuesContext);
  const isMobileView = useContext(ViewportContext);

  const {
    topDestinationslistItems,
    placeholderTextPopularDestination,
    orderingRecentlySearchedAndViewed,
    disableRecentlyViewed,
    disableRecentlySearched,
    disableTopDestination,
    domesticDestinationsListItems,
    featuredDestinationsListItems,
    domesticLabel,
    featuredLabel,
    searchViewType,
    enableNewSearch,
    recentSearchesHeading,
    recentlyViewedHeading,
    clearRecentLabel,
    collapseLabel,
    newSearchLabel,
    currentLocationLabel,
  } = useContext(ContentContext);
  const { currentLocale } = usePageContext();

  const [topDestinationsList] = useState<Array<{ topDestinationField: string }>>(topDestinationslistItems);

  const [domesticDestinationsList] = useState<
    Array<{
      displayLabel: string;
      actualLabel: string;
    }>
  >(domesticDestinationsListItems);

  const [featuredDestinationsList] = useState<
    Array<{
      displayLabel: string;
      actualLabel: string;
    }>
  >(featuredDestinationsListItems);

  const firstList = orderingRecentlySearchedAndViewed as string;
  const hideRecentSearches = disableRecentlySearched as string;
  const hideRecentlyViewed = disableRecentlyViewed as string;
  const hideTopDestinations = disableTopDestination as string;
  const PopularDestinationHeading = placeholderTextPopularDestination as string;
  const domesticDestinationHeading = domesticLabel as string;
  const featuredDestinationHeading = featuredLabel as string;
  const showSearchListView = searchViewType && searchViewType === 'defaultview';
  const showNewSearchLink = enableNewSearch === 'true';

  const getRecentSearchList: string | null = localStorage.getItem('miRecentSearch');
  const getRecentViewList: string | null = localStorage.getItem('miRecentlyViewedProperties');
  const recentlyViewedAll: Array<Record<string, string>> =
    getRecentViewList && JSON.parse(getRecentViewList)?.recentlyViewedProperties;

  const recentSearches: Array<Record<string, string>> = getRecentSearchList && JSON.parse(getRecentSearchList);
  const recentlyViewed: Array<Record<string, string>> = recentlyViewedAll?.slice(0, 5);
  const GOOGLE_MAP_API_KEY = process.env['GOOGLE_MAP_API_KEY'];
  const { envVarsObject } = useClientEnvVarsStore();
  const BAIDU_MAP_API_KEY = envVarsObject?.['BAIDU_MAP_API_KEY'] || '';

  let showRecentSearches = false;
  let showRecentlyViewed = false;
  let showTopDestinations = false;

  let recentSearchesData;
  let recentlyViewedData;

  if (recentSearches && hideRecentSearches === 'false') {
    //removing duplicate entries
    const removedDuplicates = recentSearches?.filter(
      (value, index, self) => index === self.findIndex(t => t['label'] === value['label'])
    );
    showRecentSearches = true;
    recentSearchesData = {
      heading: recentSearchesHeading,
      listArray: removedDuplicates,
      nameKey: 'label',
      valueKey: 'label',
    };
  }

  if (recentlyViewed && hideRecentlyViewed === 'false') {
    showRecentlyViewed = true;
    recentlyViewedData = {
      heading: recentlyViewedHeading,
      listArray: recentlyViewed,
      nameKey: 'name',
      valueKey: 'name',
    };
  }

  if (topDestinationsList && hideTopDestinations === 'false') {
    showTopDestinations = true;
  }

  const clearRecent = (): void => {
    localStorage.removeItem('miRecentSearch');
    localStorage.removeItem('miRecentlyViewedProperties');
    if (showSearchListView) {
      closePopup();
    } else {
      updateSearchView(true);
    }
  };

  const recentListConfig: Array<configType | undefined> = [];

  interface configType {
    heading: string;
    listArray: Array<Record<string, string>>;
    nameKey: string;
    valueKey: string;
  }

  if (firstList !== 'recentSearch') {
    if (recentlyViewedData) {
      recentListConfig.push(recentlyViewedData);
    }
    if (recentSearchesData) {
      recentListConfig.push(recentSearchesData);
    }
  } else {
    if (recentSearchesData) {
      recentListConfig.push(recentSearchesData);
    }
    if (recentlyViewedData) {
      recentListConfig.push(recentlyViewedData);
    }
  }

  const hiddenFieldsHandler = (data: Record<string, string | number>): void =>
    setFormValues(setDestinationHiddenFields(data));

  const showSearchHistory = showRecentlyViewed || showRecentSearches;

  useEffect(() => {
    if (!showSearchHistory && showSearchListView) {
      updateSearchView(true);
    }
  }, []);

  return (
    <StyledRecentViewAndSearch>
      <>
        <CurrentLocationFeature
          currentLocationHiddenFieldHandler={hiddenFieldsHandler}
          inputFieldHandler={updateInputValue}
          locale={currentLocale}
          currentLocationLabel={currentLocationLabel}
          apiKey={isCNLocale(currentLocale) ? BAIDU_MAP_API_KEY : GOOGLE_MAP_API_KEY}
          render={CurrentLocationView}
        />
        {!showSearchHistory && showTopDestinations && showSearchListView && (
          <RecentList
            heading={PopularDestinationHeading}
            listArray={topDestinationsList}
            listIcon={'location'}
            nameKey={'topDestinationField'}
            valueKey={'topDestinationField'}
            clickHandler={updateInputValue}
          />
        )}

        {(showListView || (!showSearchHistory && showSearchListView)) && (
          <>
            {domesticDestinationsList?.length > 0 && (
              <RecentList
                heading={domesticDestinationHeading}
                listArray={domesticDestinationsList}
                listIcon={'location'}
                nameKey={'displayLabel'}
                valueKey={'actualLabel'}
                clickHandler={updateInputValue}
                inlineDisplay={true}
                elementClass={'domestic-dest-list'}
              />
            )}
            {featuredDestinationsList?.length > 0 && (
              <RecentList
                heading={featuredDestinationHeading}
                listArray={featuredDestinationsList}
                listIcon={'location'}
                nameKey={'displayLabel'}
                valueKey={'actualLabel'}
                clickHandler={updateInputValue}
                inlineDisplay={true}
              />
            )}
            {!isMobileView && (
              <TertiaryCta
                clickHandler={closePopup}
                label={collapseLabel}
                className="collapse-list-view-btn icon-arrow-up"
              />
            )}
          </>
        )}

        {!showListView && recentListConfig.length
          ? recentListConfig.map(
              item =>
                item !== undefined && (
                  <RecentList
                    key={item.heading}
                    heading={item.heading}
                    listArray={item.listArray}
                    listIcon={'clock'}
                    nameKey={item.nameKey}
                    valueKey={item.valueKey}
                    clickHandler={updateInputValue}
                    hiddenFieldsHandler={hiddenFieldsHandler}
                  />
                )
            )
          : null}

        {showSearchHistory && (
          <div className={clsx('search-history-btm-cta-wrapper ', !showListView ? '' : 'd-none')}>
            <TertiaryCta clickHandler={clearRecent} label={clearRecentLabel} />
            {showSearchListView && showNewSearchLink && (
              <TertiaryCta
                clickHandler={(): void => updateSearchView(true)}
                label={newSearchLabel}
                className="icon-back-arrow-cropped new-search-btn"
              />
            )}
          </div>
        )}
      </>
    </StyledRecentViewAndSearch>
  );
};

export default RecentViewAndSearch;
